import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Storage from '../utils/storageES.js'
import HomeButton from './HomeButton.jsx'

function Lodging({ userData, onNavigate, embedded = false }) {
  const { t } = useTranslation()
  const [lodgings, setLodgings] = useState([])
  const [isAddingLodging, setIsAddingLodging] = useState(false)
  const [editingLodging, setEditingLodging] = useState(null)
  const [reviewingLodging, setReviewingLodging] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    type: 'Hotel',
    address: '',
    checkIn: '',
    checkOut: '',
    phone: '',
    email: '',
    website: '',
    notes: '',
    pricePerNight: '',
    currency: '$'
  })
  const [reviewData, setReviewData] = useState({
    rating: 5,
    title: '',
    content: '',
    pros: '',
    cons: '',
    wouldRecommend: true
  })

  const lodgingTypes = [
    { value: 'Hotel', label: t('lodging.types.hotel') },
    { value: 'Hostel', label: t('lodging.types.hostel') },
    { value: 'Apartment', label: t('lodging.types.apartment') },
    { value: 'Resort', label: t('lodging.types.resort') },
    { value: 'B&B', label: t('lodging.types.bnb') },
    { value: 'Guesthouse', label: t('lodging.types.guesthouse') },
    { value: 'Villa', label: t('lodging.types.villa') },
    { value: 'Other', label: t('lodging.types.other') }
  ]

  useEffect(() => {
    loadLodgings()
  }, [])

  const loadLodgings = () => {
    const tripData = Storage.getTripData()
    const savedLodgings = tripData.lodgings || []
    setLodgings(savedLodgings)
  }

  const saveLodgings = (lodgingsList) => {
    const tripData = Storage.getTripData()
    tripData.lodgings = lodgingsList
    Storage.saveTripData(tripData)
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleReviewChange = (field, value) => {
    setReviewData(prev => ({ ...prev, [field]: value }))
  }

  const addOrUpdateLodging = () => {
    if (!formData.name.trim() || !formData.address.trim()) {
      alert(t('lodging.fillRequiredFields'))
      return
    }

    const lodgingData = {
      id: editingLodging ? editingLodging.id : Date.now(),
      name: formData.name.trim(),
      type: formData.type,
      address: formData.address.trim(),
      checkIn: formData.checkIn,
      checkOut: formData.checkOut,
      phone: formData.phone.trim(),
      email: formData.email.trim(),
      website: formData.website.trim(),
      notes: formData.notes.trim(),
      pricePerNight: formData.pricePerNight,
      currency: formData.currency,
      dateAdded: editingLodging ? editingLodging.dateAdded : new Date().toISOString(),
      review: editingLodging ? editingLodging.review : null
    }

    let updatedLodgings
    if (editingLodging) {
      updatedLodgings = lodgings.map(lodging => 
        lodging.id === editingLodging.id ? lodgingData : lodging
      )
    } else {
      updatedLodgings = [...lodgings, lodgingData]
    }

    setLodgings(updatedLodgings)
    saveLodgings(updatedLodgings)
    resetForm()
  }

  const addReview = () => {
    if (!reviewData.title.trim() || !reviewData.content.trim()) {
      alert(t('lodging.fillReviewFields'))
      return
    }

    const review = {
      rating: reviewData.rating,
      title: reviewData.title.trim(),
      content: reviewData.content.trim(),
      pros: reviewData.pros.trim(),
      cons: reviewData.cons.trim(),
      wouldRecommend: reviewData.wouldRecommend,
      dateReviewed: new Date().toISOString()
    }

    const updatedLodgings = lodgings.map(lodging =>
      lodging.id === reviewingLodging.id ? { ...lodging, review } : lodging
    )

    setLodgings(updatedLodgings)
    saveLodgings(updatedLodgings)
    resetReview()
  }

  const editLodging = (lodging) => {
    setEditingLodging(lodging)
    setFormData({
      name: lodging.name,
      type: lodging.type,
      address: lodging.address,
      checkIn: lodging.checkIn || '',
      checkOut: lodging.checkOut || '',
      phone: lodging.phone || '',
      email: lodging.email || '',
      website: lodging.website || '',
      notes: lodging.notes || '',
      pricePerNight: lodging.pricePerNight || '',
      currency: lodging.currency || '$'
    })
    setIsAddingLodging(true)
  }

  const startReview = (lodging) => {
    setReviewingLodging(lodging)
    if (lodging.review) {
      setReviewData({
        rating: lodging.review.rating,
        title: lodging.review.title,
        content: lodging.review.content,
        pros: lodging.review.pros || '',
        cons: lodging.review.cons || '',
        wouldRecommend: lodging.review.wouldRecommend
      })
    } else {
      setReviewData({
        rating: 5,
        title: '',
        content: '',
        pros: '',
        cons: '',
        wouldRecommend: true
      })
    }
  }

  const deleteLodging = (id) => {
    if (confirm(t('lodging.confirmDelete'))) {
      const updatedLodgings = lodgings.filter(lodging => lodging.id !== id)
      setLodgings(updatedLodgings)
      saveLodgings(updatedLodgings)
    }
  }

  const openInMaps = (address) => {
    const encodedAddress = encodeURIComponent(address)
    
    // Detect if user is on iOS/Mac for Apple Maps, otherwise use Google Maps
    const isApple = /iPad|iPhone|iPod|Mac/.test(navigator.userAgent)
    
    if (isApple) {
      // Try Apple Maps first on Apple devices
      window.open(`http://maps.apple.com/?q=${encodedAddress}`, '_blank')
    } else {
      // Use Google Maps on other devices
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank')
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'Hotel',
      address: '',
      checkIn: '',
      checkOut: '',
      phone: '',
      email: '',
      website: '',
      notes: '',
      pricePerNight: '',
      currency: '$'
    })
    setIsAddingLodging(false)
    setEditingLodging(null)
  }

  const resetReview = () => {
    setReviewData({
      rating: 5,
      title: '',
      content: '',
      pros: '',
      cons: '',
      wouldRecommend: true
    })
    setReviewingLodging(null)
  }

  const formatPrice = (price, currency) => {
    if (!price) return ''
    return `${currency}${price}/night`
  }

  const getRatingStars = (rating) => {
    return '★'.repeat(rating) + '☆'.repeat(5 - rating)
  }

  // Show review form
  if (reviewingLodging) {
    return (
      <div className="container lodging-review" style={{
        background: 'linear-gradient(135deg, #f5f1eb 0%, #ede4d3 100%)',
        minHeight: '100vh',
        padding: '20px',
        position: 'relative'
      }}>

        {/* Home Button */}
        <HomeButton onNavigate={onNavigate} />

        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '30px'
        }}>
          <h1 style={{
            fontSize: '36px',
            fontWeight: '900',
            color: '#8B7355',
            margin: '0 0 8px 0',
            letterSpacing: '-1px'
          }}>{t('lodging.reviewLodging')}</h1>
          <p style={{
            fontSize: '16px',
            color: '#B8A082',
            margin: '0',
            fontWeight: '600'
          }}>{reviewingLodging.name}</p>
        </div>

        {/* Review Form */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          margin: '0 0 20px 0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>

          {/* Rating */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#8B7355',
              marginBottom: '8px'
            }}>
              {t('lodging.formOverallRating')}
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  onClick={() => handleReviewChange('rating', star)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    color: star <= reviewData.rating ? '#5B8FA8' : '#ddd',
                    cursor: 'pointer'
                  }}
                >
                  ★
                </button>
              ))}
              <span style={{
                marginLeft: '12px',
                fontSize: '14px',
                color: '#8B7355',
                fontWeight: '600'
              }}>
                {t('lodging.rating', { rating: reviewData.rating })}
              </span>
            </div>
          </div>

          {/* Review Title */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#8B7355',
              marginBottom: '6px'
            }}>
              {t('lodging.formReviewTitle')}
            </label>
            <input
              type="text"
              value={reviewData.title}
              onChange={(e) => handleReviewChange('title', e.target.value)}
              placeholder={t('lodging.placeholderReviewTitle')}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #ede4d3',
                borderRadius: '12px',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Review Content */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#8B7355',
              marginBottom: '6px'
            }}>
              {t('lodging.formDetailedReview')}
            </label>
            <textarea
              value={reviewData.content}
              onChange={(e) => handleReviewChange('content', e.target.value)}
              placeholder={t('lodging.placeholderReview')}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #ede4d3',
                borderRadius: '12px',
                fontSize: '14px',
                outline: 'none',
                minHeight: '120px',
                resize: 'vertical',
                boxSizing: 'border-box',
                fontFamily: 'inherit'
              }}
            />
          </div>

          {/* Pros */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#8B7355',
              marginBottom: '6px'
            }}>
              {t('lodging.formPros')}
            </label>
            <textarea
              value={reviewData.pros}
              onChange={(e) => handleReviewChange('pros', e.target.value)}
              placeholder={t('lodging.placeholderPros')}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #ede4d3',
                borderRadius: '12px',
                fontSize: '14px',
                outline: 'none',
                minHeight: '80px',
                resize: 'vertical',
                boxSizing: 'border-box',
                fontFamily: 'inherit'
              }}
            />
          </div>

          {/* Cons */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#8B7355',
              marginBottom: '6px'
            }}>
              {t('lodging.formCons')}
            </label>
            <textarea
              value={reviewData.cons}
              onChange={(e) => handleReviewChange('cons', e.target.value)}
              placeholder={t('lodging.placeholderCons')}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #ede4d3',
                borderRadius: '12px',
                fontSize: '14px',
                outline: 'none',
                minHeight: '80px',
                resize: 'vertical',
                boxSizing: 'border-box',
                fontFamily: 'inherit'
              }}
            />
          </div>

          {/* Would Recommend */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              fontSize: '14px',
              fontWeight: '600',
              color: '#8B7355',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={reviewData.wouldRecommend}
                onChange={(e) => handleReviewChange('wouldRecommend', e.target.checked)}
                style={{
                  marginRight: '8px',
                  accentColor: '#C4714A'
                }}
              />
              {t('lodging.recommendCheckbox')}
            </label>
          </div>

          {/* Buttons */}
          <div style={{
            display: 'flex',
            gap: '12px'
          }}>
            <button
              onClick={addReview}
              style={{
                background: '#C4714A',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '25px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                flex: '1'
              }}
            >
              {reviewingLodging.review ? t('lodging.updateReview') : t('lodging.saveReview')}
            </button>
            <button
              onClick={resetReview}
              style={{
                background: '#ccc',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '25px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                flex: '1'
              }}
            >
              {t('buttons.cancel')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  const content = (
    <>
      {/* Header */}
      {!embedded && <div style={{
        textAlign: 'center',
        marginBottom: '30px',
        paddingTop: '60px'
      }}>
        <h1 style={{
          fontSize: '36px',
          fontWeight: '900',
          color: '#8B7355',
          margin: '0 0 8px 0',
          letterSpacing: '2px',
          fontFamily: "'Noto Serif', serif",
          textTransform: 'uppercase'
        }}>{t('lodging.title')}</h1>
        <p style={{
          fontSize: '14px',
          color: '#8B7355',
          margin: '0 0 16px 0',
          opacity: '0.7'
        }}>{t('lodging.accommodationIn', { city: userData.destination?.toUpperCase() || t('mainPage.yourCity') })}</p>
        
        {/* Stats */}
        <div style={{
          background: 'white',
          borderRadius: '25px',
          padding: '16px 20px',
          margin: '0 auto 20px auto',
          maxWidth: '300px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-around',
            alignItems: 'center',
            textAlign: 'center'
          }}>
            <div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#B8A082' }}>{lodgings.length}</div>
              <div style={{ fontSize: '12px', color: '#8B7355', opacity: '0.8' }}>{t('lodging.places')}</div>
            </div>
            <div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#D4A574' }}>
                {lodgings.filter(l => l.review).length}
              </div>
              <div style={{ fontSize: '12px', color: '#8B7355', opacity: '0.8' }}>{t('lodging.reviewed')}</div>
            </div>
            <div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#8B7355' }}>
                {lodgings.filter(l => !l.review).length}
              </div>
              <div style={{ fontSize: '12px', color: '#8B7355', opacity: '0.8' }}>{t('lodging.toReview')}</div>
            </div>
          </div>
        </div>
      </div>}

      {/* Add/Edit Lodging Form */}
      {isAddingLodging ? (
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          margin: '0 0 20px 0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '700',
            color: '#B8A082',
            margin: '0 0 20px 0'
          }}>
            {editingLodging ? t('lodging.editAccommodation') : t('lodging.addNew')}
          </h3>

          {/* Name and Type */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: '2' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#8B7355',
                marginBottom: '6px'
              }}>
                {t('lodging.formName')}
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder={t('lodging.placeholderName')}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #ede4d3',
                  borderRadius: '12px',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ flex: '1' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#8B7355',
                marginBottom: '6px'
              }}>
                {t('lodging.formType')}
              </label>
              <select
                value={formData.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #ede4d3',
                  borderRadius: '12px',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box',
                  background: 'white'
                }}
              >
                {lodgingTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Address */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#8B7355',
              marginBottom: '6px'
            }}>
              {t('lodging.formAddress')}
            </label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              placeholder={t('lodging.placeholderAddress')}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #ede4d3',
                borderRadius: '12px',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Check-in and Check-out */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: '1' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#8B7355',
                marginBottom: '6px'
              }}>
                {t('lodging.formCheckIn')}
              </label>
              <input
                type="date"
                value={formData.checkIn}
                onChange={(e) => handleInputChange('checkIn', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #ede4d3',
                  borderRadius: '12px',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ flex: '1' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#8B7355',
                marginBottom: '6px'
              }}>
                {t('lodging.formCheckOut')}
              </label>
              <input
                type="date"
                value={formData.checkOut}
                onChange={(e) => handleInputChange('checkOut', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #ede4d3',
                  borderRadius: '12px',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          {/* Contact Info */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: '1' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#8B7355',
                marginBottom: '6px'
              }}>
                {t('lodging.formPhone')}
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder={t('lodging.placeholderPhone')}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #ede4d3',
                  borderRadius: '12px',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ flex: '1' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#8B7355',
                marginBottom: '6px'
              }}>
                {t('lodging.formEmail')}
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder={t('lodging.placeholderEmail')}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #ede4d3',
                  borderRadius: '12px',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          {/* Website and Price */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: '2' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#8B7355',
                marginBottom: '6px'
              }}>
                {t('lodging.formWebsite')}
              </label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => handleInputChange('website', e.target.value)}
                placeholder={t('lodging.placeholderWebsite')}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #ede4d3',
                  borderRadius: '12px',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ flex: '1', display: 'flex', gap: '8px' }}>
              <div style={{ flex: '0 0 60px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#8B7355',
                  marginBottom: '6px'
                }}>
                  {t('lodging.formCurrency')}
                </label>
                <select
                  value={formData.currency}
                  onChange={(e) => handleInputChange('currency', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 8px',
                    border: '2px solid #ede4d3',
                    borderRadius: '12px',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box',
                    background: 'white'
                  }}
                >
                  <option value="$">$</option>
                  <option value="€">€</option>
                  <option value="£">£</option>
                  <option value="¥">¥</option>
                  <option value="₹">₹</option>
                </select>
              </div>
              <div style={{ flex: '1' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#8B7355',
                  marginBottom: '6px'
                }}>
                  {t('lodging.formPricePerNight')}
                </label>
                <input
                  type="number"
                  value={formData.pricePerNight}
                  onChange={(e) => handleInputChange('pricePerNight', e.target.value)}
                  placeholder={t('lodging.placeholderPrice')}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #ede4d3',
                    borderRadius: '12px',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#8B7355',
              marginBottom: '6px'
            }}>
              {t('lodging.formNotes')}
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder={t('lodging.placeholderNotes')}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #ede4d3',
                borderRadius: '12px',
                fontSize: '14px',
                outline: 'none',
                minHeight: '80px',
                resize: 'vertical',
                boxSizing: 'border-box',
                fontFamily: 'inherit'
              }}
            />
          </div>

          {/* Buttons */}
          <div style={{
            display: 'flex',
            gap: '12px'
          }}>
            <button
              onClick={addOrUpdateLodging}
              style={{
                background: '#C4714A',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '25px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                flex: '1'
              }}
            >
              {editingLodging ? t('lodging.updateLodging') : t('lodging.addLodging')}
            </button>
            <button
              onClick={resetForm}
              style={{
                background: '#ccc',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '25px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                flex: '1'
              }}
            >
              {t('buttons.cancel')}
            </button>
          </div>
        </div>
      ) : (
        <div style={{
          textAlign: 'center',
          marginBottom: '20px'
        }}>
          <button
            onClick={() => setIsAddingLodging(true)}
            style={{
              background: '#C4714A',
              color: 'white',
              border: 'none',
              padding: '16px 32px',
              borderRadius: '30px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
          >
            {t('lodging.addNew')}
          </button>
        </div>
      )}

      {/* Lodging List */}
      {lodgings.length === 0 ? (
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '40px 20px',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏨</div>
          <h3 style={{
            fontSize: '18px',
            color: '#8B7355',
            margin: '0 0 8px 0'
          }}>{t('lodging.noAccommodations')}</h3>
          <p style={{
            fontSize: '14px',
            color: '#999',
            margin: '0'
          }}>{t('lodging.startAdding')}</p>
        </div>
      ) : (
        lodgings.map(lodging => (
          <div key={lodging.id} style={{
            background: 'white',
            borderRadius: '16px',
            padding: '20px',
            margin: '0 0 16px 0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            
            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '16px'
            }}>
              <div style={{ flex: '1' }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#333',
                  margin: '0 0 4px 0'
                }}>
                  {lodging.name}
                </h3>
                <div style={{
                  fontSize: '14px',
                  color: '#B8A082',
                  fontWeight: '600',
                  marginBottom: '8px'
                }}>
                  {lodging.type}
                  {lodging.pricePerNight && (
                    <span style={{ marginLeft: '12px', color: '#8B7355' }}>
                      {formatPrice(lodging.pricePerNight, lodging.currency)}
                    </span>
                  )}
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#666',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>{lodging.address}</span>
                  <button
                    onClick={() => openInMaps(lodging.address)}
                    style={{
                      background: '#C4714A',
                      color: 'white',
                      border: 'none',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '10px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    {t('lodging.maps')}
                  </button>
                </div>
                {(lodging.checkIn || lodging.checkOut) && (
                  <div style={{
                    fontSize: '14px',
                    color: '#8B7355',
                    fontWeight: '600'
                  }}>
                    {lodging.checkIn && t('lodging.checkInDate', { date: new Date(lodging.checkIn).toLocaleDateString() })}
                    {lodging.checkIn && lodging.checkOut && ' • '}
                    {lodging.checkOut && t('lodging.checkOutDate', { date: new Date(lodging.checkOut).toLocaleDateString() })}
                  </div>
                )}
              </div>
              
              {/* Actions */}
              <div style={{
                display: 'flex',
                gap: '8px',
                flexDirection: 'column'
              }}>
                <button
                  onClick={() => startReview(lodging)}
                  style={{
                    background: lodging.review ? '#5B8FA8' : '#D4A574',
                    color: 'white',
                    border: 'none',
                    padding: '6px 12px',
                    borderRadius: '15px',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {lodging.review ? t('lodging.editReview') : t('lodging.addReview')}
                </button>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button
                    onClick={() => editLodging(lodging)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#D4A574',
                      fontSize: '16px',
                      cursor: 'pointer',
                      padding: '4px'
                    }}
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => deleteLodging(lodging.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#ccc',
                      fontSize: '16px',
                      cursor: 'pointer',
                      padding: '4px'
                    }}
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            {(lodging.phone || lodging.email || lodging.website) && (
              <div style={{
                display: 'flex',
                gap: '16px',
                marginBottom: '12px',
                fontSize: '12px',
                color: '#666'
              }}>
                {lodging.phone && (
                  <div>
                    <strong>Phone:</strong> <a href={`tel:${lodging.phone}`} style={{ color: '#B8A082' }}>{lodging.phone}</a>
                  </div>
                )}
                {lodging.email && (
                  <div>
                    <strong>Email:</strong> <a href={`mailto:${lodging.email}`} style={{ color: '#B8A082' }}>{lodging.email}</a>
                  </div>
                )}
                {lodging.website && (
                  <div>
                    <strong>Website:</strong> <a href={lodging.website} target="_blank" rel="noopener noreferrer" style={{ color: '#B8A082' }}>Visit</a>
                  </div>
                )}
              </div>
            )}

            {/* Notes */}
            {lodging.notes && (
              <div style={{
                fontSize: '14px',
                color: '#666',
                marginBottom: '12px',
                fontStyle: 'italic'
              }}>
                {lodging.notes}
              </div>
            )}

            {/* Review */}
            {lodging.review && (
              <div style={{
                background: '#f8f6f0',
                borderRadius: '12px',
                padding: '16px',
                marginTop: '16px'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <h4 style={{
                    fontSize: '16px',
                    fontWeight: '700',
                    color: '#B8A082',
                    margin: '0'
                  }}>
                    {lodging.review.title}
                  </h4>
                  <div style={{
                    fontSize: '16px',
                    color: '#B8A082'
                  }}>
                    {getRatingStars(lodging.review.rating)}
                  </div>
                </div>
                <p style={{
                  fontSize: '14px',
                  color: '#666',
                  margin: '0 0 8px 0',
                  lineHeight: '1.4'
                }}>
                  {lodging.review.content}
                </p>
                {(lodging.review.pros || lodging.review.cons) && (
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {lodging.review.pros && (
                      <div style={{ marginBottom: '4px' }}>
                        <strong style={{ color: '#4CAF50' }}>{t('lodging.pros')}</strong> {lodging.review.pros}
                      </div>
                    )}
                    {lodging.review.cons && (
                      <div>
                        <strong style={{ color: '#f44336' }}>{t('lodging.cons')}</strong> {lodging.review.cons}
                      </div>
                    )}
                  </div>
                )}
                <div style={{
                  fontSize: '12px',
                  color: '#999',
                  marginTop: '8px'
                }}>
                  {lodging.review.wouldRecommend ? t('lodging.recommendYes') : t('lodging.recommendNo')} •
                  {t('lodging.reviewedOn', { date: new Date(lodging.review.dateReviewed).toLocaleDateString() })}
                </div>
              </div>
            )}
          </div>
        ))
      )}

      {/* Back Button */}
      {!embedded && <div style={{ textAlign: 'center', margin: '30px 0 20px 0' }}>
        <button
          style={{
            background: 'transparent',
            color: '#8B7355',
            border: '2px solid #8B7355',
            padding: '12px 24px',
            borderRadius: '25px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
          onClick={() => onNavigate('home')}
        >
          {t('navigation.backToHome')}
        </button>
      </div>}
    </>
  )

  if (embedded) return content

  return (
    <div className="container lodging-tracker" style={{
      background: 'linear-gradient(135deg, #f5f1eb 0%, #ede4d3 100%)',
      minHeight: '100vh',
      padding: '20px',
      position: 'relative'
    }}>
      <HomeButton onNavigate={onNavigate} />
      {content}
    </div>
  )
}

export default Lodging